import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SchoolRecordVm, Institution, SchoolContact, PersonPhone } from '@k12/models';
import { parseAidProgramEligibility } from '@k12/user-info';
import { Observable, switchMap, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AdminSchoolRecordService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly EmptyGuid = '00000000-0000-0000-0000-000000000000';

  // The API serializes a missing account id as an all-zeros GUID. That string is truthy, so it has to
  // be rejected explicitly or it passes for a real account.
  private toAccountId(accountId?: string | null): string | null {
    return accountId && accountId !== this.EmptyGuid ? accountId : null;
  }

  private getPrimaryPersonPhone(personPhones?: PersonPhone[] | null): PersonPhone | undefined {
    if (!personPhones?.length) {
      return undefined;
    }
    return personPhones.find((phone) => phone.isPrimary) ?? personPhones[0];
  }

  private getPrimaryPhoneNumber(personPhones?: PersonPhone[] | null): string {
    return this.getPrimaryPersonPhone(personPhones)?.phone?.phoneNumber || '';
  }

  private getPrimaryPhoneExtension(personPhones?: PersonPhone[] | null): string {
    const primaryPhone = this.getPrimaryPersonPhone(personPhones);
    return (primaryPhone?.phone?.extension ?? primaryPhone?.extension ?? '').toString();
  }

  getSchoolDetails(userId: string): Observable<SchoolRecordVm | null> {
    return this.http.get<{ entity: Institution }>(`${environment.adminApiUrl}/schools/${userId}`).pipe(
      switchMap((res) => {
        const entity = res?.entity;
        if (!entity) return of(null);
        
        const school = entity.institutionSchools?.[0] || {};

        return this.http
          .get<{ entity: SchoolContact[] }>(`${environment.adminApiUrl}/schools/${school.programPartnerId}/contacts`)
          .pipe(
            map((contactsRes) => {
              const contacts = contactsRes?.entity || [];
              const hdmaWrapper = contacts.find(c => c.isHDMA);
              const hdmaContact = hdmaWrapper?.person;

              return {
                addresses: [
                  {
                    address1: school.address1 || entity.address1 || '',
                    address2: school.address2 || entity.address2 || '',
                    city: school.city || entity.city || '',
                    description: null,
                    id: school.businessAddressId || null,
                    state: school.state || entity.state || '',
                    zip: school.zip || entity.zip || '',
                    county: school.county || ''
                  }
                ],
                businessAddressId: school.businessAddressId || null,
                schoolName: school.name || entity.name || '',
                institutionName: entity.name || '',
                // The school's HDMA account comes from its program partner row and is always set. The
                // institution's is nullable, so it is only a fallback.
                hdmaAccountId: this.toAccountId(school.hdmaAccountId) ?? this.toAccountId(entity.hdmaAccountId),
                hdmaFirstName: hdmaContact?.firstName || '',
                hdmaMiddleName: hdmaContact?.middleName || '',
                hdmaLastName: hdmaContact?.lastName || '',
                hdmaName: hdmaContact
                  ? `${hdmaContact.firstName || ''} ${hdmaContact.middleName || ''} ${hdmaContact.lastName || ''}`.trim()
                  : '',
                hdmaEmail: hdmaContact?.personEmails?.map((e) => e.emailAddress).join(', ') || '',
                phone: this.getPrimaryPhoneNumber(hdmaContact?.personPhones),
                phoneExtension: this.getPrimaryPhoneExtension(hdmaContact?.personPhones),
                schoolType: school.schoolType || '',
                schoolStatus: school.schoolStatus || '',
                schoolStatusId: school.schoolStatusId ?? null,
                fiscalYearEndDate: entity.fiscalYearEndDate || '',
                institutionSchoolCount: entity.schoolsCount || 0,
                schoolId: school.recordNumber || '',
                backgroundCheckValidationDate: school.backgroundCheckValidatedDate || '',
                aidProgramEligibility: parseAidProgramEligibility(
                  school.programType,
                  school.programTypeId,
                  school.programTypeIds
                ),
                feinNumber: entity.federalEmployerID || '',
                dnpeNumber: school.dnpeNumber || '',
                educationDeliveryMethod: school.schoolInstructionType || [],
                federalEmployerID: entity.federalEmployerID || '',
                institutionId: school.institutionId || '',
                schoolTypeId: school.schoolTypeId || null,
                operationalScheduleTypeId: school.operationalScheduleTypeId || null,
                backgroundCheckNameOnFile: school.backgroundCheckNameOnFile || '',
                tuitionFeesUploadedDate: school.tuitionFeesUploadedDate || null,
                areTuitionFeesApproved: school.areTuitionFeesApproved || false,
                verbalCommunication: school.verbalCommunication || false,
                hdmaContact: hdmaWrapper,
                isValidEmail: school.isValidEmail
              } as SchoolRecordVm;
            }),
            catchError(err => {
              console.error('Error fetching school data:', err);
              return of(null);
            })
          );
      }),
      catchError(err => {
        console.error('Error fetching school data:', err);
        return of(null);
      })
    );
  }
}
